import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, CircularProgress, Alert, Select, MenuItem,
  Box, Pagination, Chip, Button, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  Avatar, TextField
} from "@mui/material";
import {
  Person as PersonIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Flag as FlagIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  AutoAwesome as AutoGenerateIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon
} from "@mui/icons-material";
import { styled } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Authority Signature Manager Component
const AuthoritySignatureManager = ({ company_id, token }) => {
  const [signatures, setSignatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    authority_name: "",
    designation: "",
    signature_image: null,
    is_active: true
  });

  useEffect(() => {
    fetchSignatures();
  }, [company_id]);

  const fetchSignatures = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/bonafide/signatures`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSignatures(response.data);
    } catch (err) {
      setError("Failed to fetch signatures. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, signature_image: file }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('authority_name', formData.authority_name);
      formDataToSend.append('designation', formData.designation);
      
      if (formData.signature_image instanceof File) {
        formDataToSend.append('signature', formData.signature_image);
      }

      let response;
      
      if (editMode && selectedSignature) {
        // Update existing signature
        response = await axios.put(
          `${API_URL}/api/bonafide/signatures/${selectedSignature.id}`,
          formDataToSend,
          {
            headers: { 
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`
            }
          }
        );
        alert("Signature updated successfully!");
      } else {
        // Upload new signature
        response = await axios.post(
          `${API_URL}/api/bonafide/signatures/upload`,
          formDataToSend,
          {
            headers: { 
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`
            }
          }
        );
        alert("Signature uploaded successfully!");
      }
      
      handleCloseDialog();
      fetchSignatures();
      
    } catch (error) {
      //console.error("Error saving signature:", error);
      alert("Failed to save signature. Please try again.");
    }
  };

  const handleSetActive = async (id) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('is_active', 'true');
      
      await axios.put(
        `${API_URL}/api/bonafide/signatures/${id}`,
        formDataToSend,
        {
          headers: { 
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      alert("Signature set as active successfully!");
      fetchSignatures();
      
    } catch (error) {
      //console.error("Error setting active signature:", error);
      alert("Failed to set signature as active.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this signature?")) {
      return;
    }
    
    try {
      await axios.delete(
        `${API_URL}/api/bonafide/signatures/${id}`,
        {
          headers: { Authorization: `Bearer ${token}`,"content-type": "application/json"
         }
        }
      );
      
      alert("Signature deleted successfully!");
      fetchSignatures();
      
    } catch (error) {
      //console.error("Error deleting signature:", error);
      alert("Failed to delete signature.");
    }
  };

  const handleEdit = (signature) => {
    setSelectedSignature(signature);
    setFormData({
      authority_name: signature.authority_name,
      designation: signature.designation,
      signature_image: null,
      is_active: signature.is_active
    });
    setPreviewImage(signature.signature_image ? 
      `${API_URL}/${signature.signature_image}` : null);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleViewImage = (imagePath) => {
    setPreviewImage(`${API_URL}/${imagePath}`);
    setPreviewOpen(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedSignature(null);
    setFormData({
      authority_name: "",
      designation: "",
      signature_image: null,
      is_active: true
    });
    setPreviewImage(null);
  };

  const handleOpenAddDialog = () => {
    setEditMode(false);
    setFormData({
      authority_name: "",
      designation: "",
      signature_image: null,
      is_active: true
    });
    setPreviewImage(null);
    setOpenDialog(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Authority Signature Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add New Signature
        </Button>
      </Box>

      {isLoading && <CircularProgress sx={{ display: "block", mx: "auto", my: 3 }} />}
      {error && <Alert severity="error">{error}</Alert>}

      {!isLoading && !error && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Signature</strong></TableCell>
                <TableCell><strong>Authority Name</strong></TableCell>
                <TableCell><strong>Designation</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Uploaded On</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signatures.map((sig) => (
                <TableRow key={sig.id}>
                  <TableCell>
                    <Avatar
                      src={`${API_URL}/${sig.signature_image}`}
                      variant="rounded"
                      sx={{ width: 50, height: 40, cursor: 'pointer' }}
                      onClick={() => handleViewImage(sig.signature_image)}
                    />
                  </TableCell>
                  <TableCell>{sig.authority_name}</TableCell>
                  <TableCell>{sig.designation}</TableCell>
                  <TableCell>
                    <Chip
                      label={sig.is_active ? "Active" : "Inactive"}
                      color={sig.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(sig.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                  
                    {!sig.is_active && (
                      <Tooltip title="Set as Active">
                        <IconButton 
                          size="small" 
                          color="success"
                          onClick={() => handleSetActive(sig.id)}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEdit(sig)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDelete(sig.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {signatures.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">
                      No signatures uploaded yet. Click "Add New Signature" to upload.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? "Edit Signature" : "Upload New Signature"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Authority Name"
              name="authority_name"
              value={formData.authority_name}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Designation"
              name="designation"
              value={formData.designation}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            
            <Box sx={{ mt: 2 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {editMode ? "Change Signature Image" : "Upload Signature Image"}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Allowed formats: JPEG, PNG, GIF (Max 2MB)
              </Typography>
            </Box>

            {previewImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
                <img 
                  src={previewImage} 
                  alt="Signature Preview"
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '100px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px'
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.authority_name || !formData.designation || (!editMode && !formData.signature_image)}
          >
            {editMode ? "Update" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>
          Signature Preview
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <img 
              src={previewImage} 
              alt="Signature"
              style={{ maxWidth: '100%', maxHeight: '400px' }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Main BonafideRequestList Component
const BonafideRequestList = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [companyId, setCompanyId] = useState(null);
  const [token, setToken] = useState(null);
  
  const limit = 5;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
    
    setToken(token);
    const payload = JSON.parse(atob(token.split('.')[1]));
    setCompanyId(payload.company_id);
    
    fetchRequests();
  }, [page]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/bonafide?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setRequests(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      setError("Failed to fetch bonafide requests. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
  
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      setIsLoading(true);
  
      const response = await axios.put(
        `${API_URL}/api/bonafide/${id}/status`,
        { status }, // request body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
  
      alert(
        `Bonafide request has been ${status}${
          response.data.certificateGenerated
            ? ' and certificate generated automatically'
            : ''
        }`
      );
  
      await fetchRequests();
  
    } catch (error) {
      //console.error("Error updating status:", error);
  
      if (error.response?.status === 401) {
        alert("Unauthorized. Please login again.");
      } else {
        alert("Failed to update status.");
      }
  
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualGenerate = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.post(
        `${API_URL}/api/bonafide/${id}/generate-certificate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}`,
                    "content-type": "application/json" }
        }
      );
      
      alert('Certificate generated successfully!');
      fetchRequests(); // Refresh the list
    } catch (error) {
      //console.error('Error generating certificate:', error);
      alert('Failed to generate certificate. Please try again.');
    }
  };

  const handleFileUpload = async (event, id) => {
    const file = event.target.files[0];
    if (!file) return;
  
    const formData = new FormData();
    formData.append('certificate', file);
  
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.post(
        `${API_URL}/api/bonafide/${id}/certificate`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      alert('Certificate uploaded and email sent successfully!');
      fetchRequests(); // Refresh the list
    } catch (error) {
      //console.error('Error uploading certificate:', error);
      alert('Failed to upload certificate. Please try again.');
    }
  };

  const handleViewCertificate = async (id, certificatePath) => {
    if (certificatePath && certificatePath.startsWith('http')) {
      window.open(certificatePath, "_blank");
      return;
    }

    try {
      const token = localStorage.getItem('token');
  
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const response = await axios.get(
        `${API_URL}/api/bonafide/certificates/${id}/view`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
  
      // Create blob URL
      const file = new Blob([response.data], {
        type: "application/pdf"
      });
  
      const fileURL = URL.createObjectURL(file);
  
      // Open PDF in new tab
      window.open(fileURL, "_blank");
  
    } catch (error) {
      //console.error("Error viewing certificate:", error);
  
      if (error.response?.status === 401) {
        alert("Unauthorized. Please login again.");
      } else {
        alert("Failed to view certificate.");
      }
    }
  };

  const handleDownloadCertificate = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/bonafide/certificates/${id}/download`,
        { 
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bonafide_certificate_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      //console.error('Error downloading certificate:', error);
      alert('Failed to download certificate.');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4 }}>
        <Typography variant="h4" gutterBottom  fontWeight="bold" sx={{ mb: 3 }}>
          Bonafide Requests list
        </Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}   variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile>
            <Tab label="Requests List" />
            <Tab label="Authority Signatures" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box>
            {isLoading && <CircularProgress sx={{ display: "block", mx: "auto", my: 3 }} />}
            {error && <Alert severity="error">{error}</Alert>}

            {!isLoading && !error && (
              <>
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {[
                          { icon: <PersonIcon />, text: "Name" },
                          { icon: <BadgeIcon />, text: "Employee ID" },
                          { icon: <EmailIcon />, text: "Candidate Email" },
                          { icon: <BusinessIcon />, text: "Department" },
                          { icon: <DescriptionIcon />, text: "Reason" },
                          { icon: <FlagIcon />, text: "Status" },
                          { icon: <PdfIcon />, text: "Certificate" },
                          { icon: <SettingsIcon />, text: "Actions" }
                        ].map((header, index) => (
                          <TableCell key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {React.cloneElement(header.icon, { sx: { color: 'black', fontSize: 20 } })}
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'black' }}>
                                {header.text}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>{request.name}</TableCell>
                          <TableCell>{request.employee_id}</TableCell>
                          <TableCell>{request.candidateEmail}</TableCell>
                          <TableCell>{request.department}</TableCell>
                          <TableCell>{request.reason}</TableCell>
                          <TableCell>
                            <Chip
                              label={request.status || "Pending"}
                              color={getStatusColor(request.status)}
                              variant="outlined"
                              sx={{ 
                                fontWeight: 600,
                                borderWidth: 2,
                                minWidth: 100
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {request.certificate ? (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="View Certificate">
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleViewCertificate(request.id, request.certificate)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download Certificate">
                                  <IconButton 
                                    size="small" 
                                    color="secondary"
                                    onClick={() => handleDownloadCertificate(request.id)}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                Not Generated
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Select
                                value={request.status || "Pending"}
                                onChange={(e) => handleStatusChange(request.id, e.target.value)}
                                size="small"
                                sx={{ minWidth: 120 }}
                              >
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Approved">Approve</MenuItem>
                                <MenuItem value="Rejected">Reject</MenuItem>
                              </Select>
                              
                              {request.status === 'Approved' && !request.certificate && (
                                <>
                                  <Tooltip title="Auto Generate Certificate">
                                    <Button
                                      variant="contained"
                                      color="success"
                                      size="small"
                                      startIcon={<AutoGenerateIcon />}
                                      onClick={() => handleManualGenerate(request.id)}
                                    >
                                      Generate
                                    </Button>
                                  </Tooltip>
                                  
                                  <Tooltip title="Manual Upload">
                                    <Button
                                      component="label"
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      startIcon={<CloudUploadIcon />}
                                    >
                                      Upload
                                      <VisuallyHiddenInput
                                        type="file"
                                        accept="application/pdf"
                                        onChange={(e) => handleFileUpload(e, request.id)}
                                      />
                                    </Button>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </>
            )}
          </Box>
        )}

        {tabValue === 1 && companyId && token && (
          <AuthoritySignatureManager company_id={companyId} token={token} />
        )}
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Certificate Preview
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewUrl && (
            <iframe
              src={previewUrl}
              style={{ width: '100%', height: '70vh', border: 'none' }}
              title="Certificate Preview"
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default BonafideRequestList;